import { useMemo } from 'react'

import { useConvexAnswersForResponse } from '../services/assessments.convex'
import { Skeleton } from '@/components/ui/skeleton'

export interface CategoryContentProps {
  category: string
  questions: Array<{
    id: string
    name: string
    subName: string
    managerText: string
    directReportText: string
  }>
  assessment: {
    responses?: Array<{
      id: string
      userId: string
    }> | null
    managerId: string
    employeeId: string
  }
  viewAsManager: boolean
  replaceManagerPlaceholders: (text: string) => string
  replaceOrganizationName: (text: string) => string
  formatScore: (score: number) => string
  shouldFetch: boolean
}

export function CategoryContent({
  questions,
  assessment,
  viewAsManager,
  replaceManagerPlaceholders,
  replaceOrganizationName,
  formatScore,
  shouldFetch,
}: CategoryContentProps) {
  // Get manager and direct report response IDs
  const responses = assessment.responses ?? []
  const managerResponse = responses.find(
    (r) => r.userId === assessment.managerId,
  )
  const directReportResponse = responses.find(
    (r) => r.userId === assessment.employeeId,
  )

  // Fetch all answers for each response from Convex (skipped until accordion opens).
  // Convex deduplicates subscriptions, so multiple CategoryContent components
  // sharing the same responseId only create one server subscription.
  const managerAnswers = useConvexAnswersForResponse(
    managerResponse?.id,
    !shouldFetch,
  )
  const directReportAnswers = useConvexAnswersForResponse(
    directReportResponse?.id,
    !shouldFetch,
  )

  const isLoadingManagerAnswers =
    shouldFetch && managerResponse && managerAnswers === undefined
  const isLoadingDirectReportAnswers =
    shouldFetch && directReportResponse && directReportAnswers === undefined

  // Group by subcategory and merge answers
  const subcategoryGroups = useMemo(() => {
    if (!managerAnswers && !directReportAnswers) return []

    const groups = new Map<
      string,
      Array<{
        questionId: string
        questionText: string
        managerScore: number | null
        directReportScore: number | null
      }>
    >()

    // Create map of question ID to scores (filtered to this category's question IDs)
    const questionIds = new Set(questions.map((q) => q.id))
    const scoreMap = new Map<
      string,
      { managerScore: number | null; directReportScore: number | null }
    >()

    managerAnswers
      ?.filter((a) => questionIds.has(a.questionId))
      .forEach((answer) => {
        scoreMap.set(answer.questionId, {
          managerScore: answer.answerValue,
          directReportScore:
            scoreMap.get(answer.questionId)?.directReportScore ?? null,
        })
      })

    directReportAnswers
      ?.filter((a) => questionIds.has(a.questionId))
      .forEach((answer) => {
        const existing = scoreMap.get(answer.questionId)
        scoreMap.set(answer.questionId, {
          managerScore: existing?.managerScore ?? null,
          directReportScore: answer.answerValue,
        })
      })

    // Build subcategory groups
    questions.forEach((question) => {
      const subcategory = question.subName
      const scores = scoreMap.get(question.id) ?? {
        managerScore: null,
        directReportScore: null,
      }

      const questionText = viewAsManager
        ? replaceManagerPlaceholders(question.managerText)
        : replaceOrganizationName(question.directReportText)

      if (!groups.has(subcategory)) {
        groups.set(subcategory, [])
      }

      groups.get(subcategory)!.push({
        questionId: question.id,
        questionText,
        ...scores,
      })
    })

    // Calculate averages per subcategory
    return Array.from(groups.entries()).map(
      ([subcategory, questionsInSubcategory]) => {
        const relevantScores = questionsInSubcategory
          .map((q) => (viewAsManager ? q.managerScore : q.directReportScore))
          .filter((s): s is number => s !== null)

        const averageScore =
          relevantScores.length > 0
            ? relevantScores.reduce((sum, score) => sum + score, 0) /
              relevantScores.length
            : 0

        return {
          subcategory,
          questions: questionsInSubcategory,
          averageScore,
        }
      },
    )
  }, [
    managerAnswers,
    directReportAnswers,
    questions,
    viewAsManager,
    replaceManagerPlaceholders,
    replaceOrganizationName,
  ])

  // Loading state
  if (isLoadingManagerAnswers || isLoadingDirectReportAnswers) {
    return (
      <div className="space-y-8 pt-4">
        {/* Subcategory Skeleton */}
        {[1, 2].map((i) => (
          <div key={i} className="space-y-4">
            {/* Subcategory Header */}
            <div className="flex items-center justify-between pb-3 border-b-2">
              <Skeleton className="h-5 w-32" />
              <div className="flex items-baseline gap-2">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-5 w-20" />
              </div>
            </div>

            {/* Table Headers */}
            <div className="grid grid-cols-12 gap-4 px-4 pb-2">
              <div className="col-span-8">
                <Skeleton className="h-3 w-20" />
              </div>
              <div className="col-span-2 flex justify-center">
                <Skeleton className="h-3 w-16" />
              </div>
              <div className="col-span-2 flex justify-center">
                <Skeleton className="h-3 w-20" />
              </div>
            </div>

            {/* Question Rows */}
            <div className="space-y-3">
              {[1, 2, 3].map((j) => (
                <div
                  key={j}
                  className="grid grid-cols-12 gap-4 p-4 rounded-md bg-card/50"
                >
                  <div className="col-span-8 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>
                  <div className="col-span-2 flex items-center justify-center">
                    <Skeleton className="w-12 h-12 rounded-lg" />
                  </div>
                  <div className="col-span-2 flex items-center justify-center">
                    <Skeleton className="w-12 h-12 rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // No data state
  if (subcategoryGroups.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        No answers found for this category.
      </div>
    )
  }

  return (
    <div className="space-y-8 pt-4">
      {subcategoryGroups.map((subcategory) => (
        <div key={subcategory.subcategory} className="space-y-4">
          {/* Subcategory Header */}
          <div className="flex items-center justify-between pb-3 border-b-2">
            <h4 className="font-semibold text-base text-foreground">
              {subcategory.subcategory}
            </h4>
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Awarded score:
              </span>
              <span className="font-bold text-lg">
                {formatScore(subcategory.averageScore)}
              </span>
            </div>
          </div>

          {/* Questions List */}
          <div className="space-y-3">
            <div className="grid grid-cols-12 gap-4 px-4 pb-2">
              <div className="col-span-8 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Question
              </div>
              <div className="col-span-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Manager
              </div>
              <div className="col-span-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                DirectReport
              </div>
            </div>

            {subcategory.questions.map((question, qIndex) => (
              <div
                key={qIndex}
                className="grid grid-cols-12 gap-4 p-4 rounded-md bg-card/50 hover:bg-card/80 transition-colors"
              >
                <div className="col-span-8">
                  <p className="text-sm leading-relaxed text-foreground">
                    {question.questionText}
                  </p>
                </div>
                <div className="col-span-2 flex items-center justify-center">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-chart-1/20 border border-chart-1/30">
                    <span className="font-bold text-base text-chart-1">
                      {question.managerScore !== null
                        ? question.managerScore
                        : '-'}
                    </span>
                  </div>
                </div>
                <div className="col-span-2 flex items-center justify-center">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-chart-2/20 border border-chart-2/30">
                    <span className="font-bold text-base text-chart-2">
                      {question.directReportScore !== null
                        ? question.directReportScore
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
